<template>
  <div
    style="overflow-y: hidden; background: rgba(0, 0, 0, 1)"
    class="full-width full-height"
    ref="scroller"
  >
    <div
      class="full-width full-height absolute-position column justify-center items-center content-center"
      v-if="loading"
    >
      <q-spinner-dots size="3em"></q-spinner-dots>
    </div>
    <img :style="`cursor: col-resize; ${imgStyle ? imgStyle : ''}`" ref="img" />
  </div>
</template>

<script>
import anime from "animejs";
import mixin from "../../d-card-mixins";
export default {
  name: "PhotoHorizontalSlider",
  mixins: [mixin],
  props: {
    behavior: {
      type: String,
      default: "agent",
    },
    src: {
      type: Promise,
      required: true,
    },
    centered: {
      type: Boolean,
      default: true,
    },
    initialAnimation: {
      type: Boolean,
      default: false,
    },
    smoothScroll: {
      type: Boolean,
      default: true,
    },
    wheelSpeed: {
      type: Number,
      default: 0.5,
    },
    imgStyle: {
      type: String,
      default: null,
    },
    section: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      lastPosition: 0,
      maxScrollerWidth: 0,
      scroller: null,
      mouseIsDown: false,
      mouseStartX: 0,
      loading: false,
      touching: false,
      unwatch: null,
    };
  },
  computed: {
    scrollLeft: {
      get() {
        if (this.behavior === "consumer") {
          return this.$cardStore.get(`componentOverlay.fscstate.scrollLeft`);
        } else {
          return this.lastPosition;
        }
      },
      set(val) {
        if (this.behavior === "consumer") {
          this.$cardStore.set(`componentOverlay.fscstate.scrollLeft`, val);
        } else {
          this.lastPosition = val;
        }
      },
    },
  },
  mounted() {
    this.load();
  },
  destroyed() {
    this.removeWatcher();
  },
  methods: {
    async load() {
      this.loading = true;
      const preloadImg = new Image();
      preloadImg.src = await this.src;
      preloadImg.onload = () => {
        const img = this.$refs.img;
        if (img?.style === undefined) return;
        img.style.visibility = "hidden";
        img.draggable = false;
        img.src = preloadImg.src;
        if (this.$q.platform.is.mobile) {
          img.addEventListener("touchstart", this.onTouchStart);
          document.addEventListener("touchend", this.onTouchEnd);
        } else {
          img.addEventListener("wheel", this.onWheel);
          img.addEventListener("mousedown", this.onMouseDown);
          document.addEventListener("mousemove", this.onMouseMove);
          document.addEventListener("mouseup", this.onMouseUp);
        }
        img.onload = () => {
          this.loading = false;
          this.scroller = this.$refs.scroller;
          if (this.scroller?.style === undefined) return;
          this.scroller.style.overflowY = "hidden";
          this.scroller.style.overflowX = "hidden";
          const parentRect =
            this.scroller.parentElement.getBoundingClientRect();
          this.scroller.style.width = `${parentRect.width}px`;
          this.scroller.style.height = `${parentRect.height}px`;
          img.style.height = `${parentRect.height}px`;
          this.maxScrollerWidth =
            this.scroller.scrollWidth - this.scroller.clientWidth;
          let initialPosition = 0;
          if (this.centered) {
            initialPosition = this.maxScrollerWidth / 2;
          }
          this.scroller.scrollLeft = initialPosition;
          if (this.behavior === "consumer") {
            this.lastPosition = initialPosition;
            this.addWatcher();
          } else {
            this.lastPosition = initialPosition;
          }
          img.style.visibility = "visible";
          if (this.initialAnimation) {
            this.doInitialAnimation();
          } else {
            this.scroller.style.overflowX = "auto";
          }
        };
      };

      preloadImg.onerror = () => {
        const src = this.onError(ev, this.cid, this.image.section, {
          returnValue: true,
        });

        if (preloadImg.src === src) {
          return;
        }

        preloadImg.src = src;
      };
    },
    addWatcher() {
      this.unwatch = this.$watch("scrollLeft", (val) => {
        this.scrollAnimated(val);
      });
    },
    removeWatcher() {
      if (!this?.unwatch) return;
      this.unwatch();
    },
    onWheel(ev) {
      clearTimeout(this.moveTimeout);
      let left = this.lastPosition + ev.wheelDelta * this.wheelSpeed;
      if (left <= 0) left = 0;
      if (left >= this.maxScrollerWidth) left = this.maxScrollerWidth;
      this.lastPosition = left;
      if (this.smoothScroll) {
        this.scrollAnimated(this.lastPosition, false);
      } else {
        this.scroller.scrollLeft = this.lastPosition;
      }
      this.moveTimeout = setTimeout(() => {
        this.setScrollLeft(this.lastPosition, true);
      }, 500);
    },
    onMouseDown(ev) {
      this.mouseIsDown = true;
      this.mouseStartX = -ev.pageX - this.lastPosition;
    },
    onMouseMove(ev) {
      if (!this.mouseIsDown) return;
      ev.preventDefault();
      let left = -(ev.pageX + this.mouseStartX);
      if (left <= 0) left = 0;
      if (left >= this.maxScrollerWidth) left = this.maxScrollerWidth;
      this.lastPosition = left;
      this.scroller.scrollLeft = this.lastPosition;
    },
    onMouseUp(ev) {
      this.mouseIsDown = false;
      this.setScrollLeft(this.lastPosition);
    },
    onTouchStart() {
      this.touching = true;
    },
    onTouchEnd() {
      this.touching = false;
      this.setScrollLeft(this.scroller.scrollLeft);
    },
    scrollAnimated(scrollLeft, emit = false) {
      anime({
        targets: this.scroller,
        scrollLeft: scrollLeft,
        duration: 300,
        easing: "linear",
        complete: () => {
          if (emit) this.setScrollLeft(scrollLeft);
        },
      });
    },
    setScrollLeft(left) {
      if (this.behavior === "consumer") {
        this.scrollLeft = left;
      }
    },
    doInitialAnimation() {
      const image = this.$refs.img;
      anime({
        targets: image,
        scale: [0, 1],
        duration: 800,
        easing: "easeInOutExpo",
        changeComplete: () => {
          this.scroller.style.overflowX = "auto";
        },
      });
    },
  },
};
</script>

<style scoped>
::-webkit-scrollbar {
  height: 4px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.7);
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.4);
}
</style>
